import React from 'react';
import styled, { css } from 'styled-components';

import { QuoteCardTabIcons } from './QuoteCardTabIcons';

export type TabType = 'settings' | 'transcript' | 'templates';

interface QuoteCardTabsProps {
  children: (activeTab: TabType) => React.ReactNode;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
}

const TAB_LABELS: Record<TabType, string> = {
  settings: 'Edit',
  transcript: 'Transcript',
  templates: 'Template',
};

const TAB_ORDER: TabType[] = ['settings', 'transcript', 'templates'];

export const QuoteCardTabs: React.FC<QuoteCardTabsProps> = ({
  children,
  activeTab,
  setActiveTab,
}) => {
  return (
    <Container>
      <TabsHeader>
        {TAB_ORDER.map(tab => (
          <Tab
            key={tab}
            type="button"
            $isActive={activeTab === tab}
            onClick={() => setActiveTab(tab)}
          >
            {QuoteCardTabIcons[tab]}
            {TAB_LABELS[tab]}
          </Tab>
        ))}
      </TabsHeader>
      <TabContent>{children(activeTab)}</TabContent>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const TabsHeader = styled.div`
  display: flex;
  gap: 0 var(--spacing-lg);
  align-items: center;
  width: 100%;
  border-bottom: 1px solid var(--border-subtle);
`;

const Tab = styled.button<{ $isActive: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: var(--spacing-sm) 0;
  margin: 0;
  cursor: pointer;
  border: none;
  background: none;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  transition: color var(--transition-fast);

  svg {
    flex-shrink: 0;
    opacity: 0.6;
  }

  &:hover {
    color: var(--foreground);

    svg {
      opacity: 1;
    }
  }

  ${props =>
    props.$isActive &&
    css`
      color: var(--foreground);
      font-weight: var(--font-weight-medium);

      svg {
        opacity: 1;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: var(--spacing-2xs);
        background-color: var(--accent);
        border-radius: 1px;
      }
    `}
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
